import React, { useState, useMemo } from 'react';
import { DndContext, DragOverlay, pointerWithin } from '@dnd-kit/core';
import { Maximize2, Minimize2 } from 'lucide-react';
import useTrainingPlanStore from '../../stores/trainingPlanStore';
import WeekRow from './WeekRow';
import WorkoutCard from './WorkoutCard';

export default function PlanWeekGrid() {
 const weekOrder = useTrainingPlanStore(state => state.weekOrder);
 const weeks = useTrainingPlanStore(state => state.weeks);
 const moveWorkoutToSlot = useTrainingPlanStore(state => state.moveWorkoutToSlot);

 const language = localStorage.getItem('language') || 'english';
 const [truncateEmpty, setTruncateEmpty] = useState(false);
 const [activeDragItem, setActiveDragItem] = useState(null);

 const emptyColumns = useMemo(() => {
 if (!truncateEmpty) return new Set();
 const emptySet = new Set([0, 1, 2, 3, 4, 5, 6]);
 weekOrder.forEach(wId => {
 const week = weeks[wId];
 week?.slots.forEach((slot, index) => {
 if (slot.kind !== 'empty' && slot.kind !== 'rest') {
 emptySet.delete(index);
 }
 });
 });
 return emptySet;
 }, [truncateEmpty, weekOrder, weeks]);

 const handleDragStart = (event) => {
 const { active } = event;
 if (active.data.current?.type === 'workout_card') {
 setActiveDragItem(active.data.current);
 }
 };

 const handleDragEnd = (event) => {
 const { active, over } = event;
 setActiveDragItem(null);
 if (!over) return;

 if (active.data.current?.type === 'workout_card' && over.data.current?.type === 'day_cell') {
 const sourceWeek = active.data.current.weekId;
 const sourceSlot = active.data.current.slotIndex;
 const destWeek = over.data.current.weekId;
 const destSlot = over.data.current.slotIndex;

 if (sourceWeek === destWeek && sourceSlot === destSlot) return;

 moveWorkoutToSlot(sourceWeek, sourceSlot, destWeek, destSlot);
 }
 };

 return (
 <DndContext
 onDragStart={handleDragStart}
 onDragEnd={handleDragEnd}
 collisionDetection={pointerWithin}
 >
 <div className="flex flex-col gap-4">
 <div className="flex gap-2 mb-2">
 <div className="lg:w-40 shrink-0 flex items-end justify-end lg:p-2">
 <button
 onClick={() => setTruncateEmpty(!truncateEmpty)}
                  className="flex items-center gap-1.5 min-h-11 px-3 py-1.5 rounded-lg bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-xs font-semibold text-ink-muted dark:text-ink-muted-dark hover:text-ink dark:hover:text-ink-dark transition-colors outline-none focus-visible:ring-2 focus-visible:ring-forest"
 title={language === 'hebrew' ? (truncateEmpty ? 'הצג את כל הימים' : 'הסתר ימי מנוחה') : (truncateEmpty ? 'Show all days' : 'Hide rest days')}
 >
 {truncateEmpty ? <Maximize2 size={14} /> : <Minimize2 size={14} />}
 {truncateEmpty ? (language === 'hebrew' ? 'הצג הכל' : 'Show all') : (language === 'hebrew' ? 'הסתר מנוחה' : 'Hide rest')}
 </button>
 </div>

 <div className="hidden lg:flex flex-1 gap-2">
 {Array.from({ length: 7 }).map((_, index) => {
 const isHidden = emptyColumns.has(index);
 const hebrewDays = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
 const englishDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
 const dayName = language === 'hebrew' ? hebrewDays[index] : englishDays[index];

 return (
 <div
 key={index}
 className={`transition-[opacity,transform] duration-300 ease-out ${isHidden ? 'w-0 opacity-0 overflow-hidden' : 'flex-1 min-w-[120px] opacity-100'} text-center`}
 >
 <span className="text-xs font-bold uppercase tracking-widest text-ink-muted dark:text-ink-muted">
 {dayName}
 </span>
 </div>
 );
 })}
 </div>
 </div>

 <div className="flex flex-col">
 {weekOrder.length === 0 ? (
            <div className="rounded-md border border-line dark:border-line-dark bg-surface dark:bg-surface-dark px-6 py-10 text-center text-sm font-medium text-ink-muted">
 {language === 'hebrew' ? 'אין שבועות. עדכנו את משך התוכנית בהגדרות.' : 'No weeks yet. Set duration in Edit Info.'}
 </div>
 ) : (
 weekOrder.map((wId) => (
 <WeekRow key={wId} weekId={wId} emptyColumns={emptyColumns} />
 ))
 )}
 </div>
 </div>

 <DragOverlay dropAnimation={{ duration: 200, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
 {activeDragItem ? (
 <div className="w-[180px] h-[140px] opacity-90 pointer-events-none" style={{ transform: 'scale(1.05)' }}>
 <WorkoutCard
 workoutId={activeDragItem.workoutId}
 weekId={activeDragItem.weekId}
 slotIndex={activeDragItem.slotIndex}
 />
 </div>
 ) : null}
 </DragOverlay>
 </DndContext>
 );
}
